import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { format } from 'date-fns';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from 'src/components/custom-dialog';
import EmptyContent from 'src/components/empty-content';

import Iconify from 'src/components/iconify';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';
import { useBoolean } from 'src/hooks/use-boolean';
import { paths } from 'src/routes/paths';
import CalendarEventEditor from 'src/sections/calendar/modal/calendar-event-editor';
import { useDeleteCalendarEventMutation, useGetCalendarEventByIdQuery } from 'src/services/calendar/calendar.service';
import { CalendarEventAvailability, CalendarEventVisibility } from 'src/services/calendar/calendar.types';
import { getEventStatusColor, getEventTypeColor } from 'src/services/calendar/calendar.utils';
import { closeCalendarEventViewer } from 'src/store/slices/app/appSlice';
import { useAppDispatch } from 'src/store/store';

// ----------------------------------------------------------------------

interface CalendarEventViewerProps {
  eventId: string;
  open: boolean;
}

export default function CalendarEventViewer({
  eventId,
  open
}: CalendarEventViewerProps) {

  const { t } = useTranslation();

  const theme = useTheme();

  const dispatch = useAppDispatch();

  const { account } = useAuthContext();

  const deleteConfirmControl = useBoolean();

  const eventEditorControl = useBoolean();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [error, setError] = useState<string | null>(null);

  const [deleteEvent, { isLoading: isDeleting }] = useDeleteCalendarEventMutation();

  const { data: event, isLoading } = useGetCalendarEventByIdQuery(
    { eventId },
    { skip: !open || !eventId }
  );

  const getEventTypeLabel = (type: string): string => t(`calendar.event_types.${type.toLowerCase()}`);

  const getEventStatusLabel = (status: string): string => t(`calendar.event_status.${status.toLowerCase()}`);

  const formatEventTime = (startDate: Date, endDate?: Date, allDay?: boolean): string => {
    if (allDay) {
      return t('calendar.all_day');
    }

    const startTime = moment(startDate).tz(account.timezone).format('h:mm a');

    if (endDate) {
      const endTime = moment(endDate).tz(account.timezone).format('h:mm a');
      return `${startTime} - ${endTime} (${account.timezone})`;
    }

    return startTime;
  };

  const formatEventDate = (date: Date): string => moment(date).tz(account.timezone).format('Do of MMMM, yyyy');

  const handleClose = () => {
    dispatch(closeCalendarEventViewer());
  };

  const handleEditEvent = () => {
    if (!event) return;

    eventEditorControl.onTrue();
  }

  const handleCloseEditor = () => {
    eventEditorControl.onFalse();
  }

  const handleDeleteEvent = async () => {
    if (!event) return;

    try {
      await deleteEvent({
        eventId: event.id,
        calendarId: event.calendar.id
      }).unwrap();

      enqueueSnackbar(t('calendar.api.delete_event.success'), {
        variant: 'success',
      });

      handleClose();

    }
    catch (err) {

      enqueueSnackbar(t('calendar.api.delete_event.default_error'), {
        variant: 'error',
      });

    }
  }

  if (isLoading) {
    return (
      <Dialog
        open={open}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
      >
        <DialogContent sx={{ py: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
    >
      <DialogContent sx={{ py: 3 }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )
        }

        {
          (!isLoading && error) && (
            <Box sx={{ py: 2 }}>
              <Typography color="error">{error}</Typography>
            </Box>
          )
        }

        {
          (!isLoading && event) && (
            <Stack spacing={3}>
              <Box>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Chip
                    label={`Type: ${getEventTypeLabel(event.type)}`}
                    size="small"
                    sx={{
                      bgcolor: alpha(getEventTypeColor(event.type, theme), 0.1),
                      color: getEventTypeColor(event.type, theme),
                      fontWeight: 'bold',
                    }}
                  />
                </Stack>

                <Stack direction="column">
                  <Typography variant="h5">
                    {event.title}
                  </Typography>

                  <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {formatEventDate(new Date(event.start_at))}
                    </Typography>

                    <Chip variant='soft' label={t(`calendar.availability_type.${event.availability_type}`)} size="small" color={event.availability_type === CalendarEventAvailability.BUSY ? 'error' : 'success'} />

                    {event.show_as && (
                      <Chip
                        variant='outlined'
                        size="small"
                        label={({
                          free: 'Free',
                          workingElsewhere: 'Working elsewhere',
                          tentative: 'Tentative',
                          busy: 'Busy',
                          oof: 'Out of office',
                        } as Record<string, string>)[event.show_as] || event.show_as}
                      />
                    )}
                  </Stack>
                </Stack>

                <Stack spacing={2}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Iconify icon="mdi:clock-outline" sx={{ mr: 1, width: 20, height: 20, color: 'text.secondary' }} />
                    {formatEventTime(event.start_at, event.end_at, event.all_day)}
                  </Typography>

                  {event.location && (
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Iconify icon="mdi:map-marker" sx={{ mr: 1, width: 20, height: 20, color: 'text.secondary' }} />
                      {event.location}
                    </Typography>
                  )}

                  {event.external_url && (
                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Iconify icon="stash:link-solid" sx={{ mr: 1, width: 20, height: 20, color: 'text.secondary' }} />
                      <Box
                        component="a"
                        href={event.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'primary.main', textDecoration: 'none' }}
                      >
                        {t('calendar.external_link')}
                      </Box>
                    </Typography>
                  )}

                  {event.meeting && (
                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Iconify icon="mdi:video" sx={{ mr: 1, width: 20, height: 20, color: 'text.secondary' }} />
                      <Box
                        component="a"
                        href={paths.dashboard.notetaker.detail(event.meeting.id as string)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'primary.main', textDecoration: 'none' }}
                      >
                        {t('calendar.view_meeting')}
                      </Box>
                    </Typography>
                  )}
                </Stack>
              </Box>

              {event.description && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {t('calendar.editor.description')}
                    </Typography>
                    <TruncatedTextField
                      text={(event.description || '').trim()}
                      mode="lines"
                      limit={4}
                      variant='body2'
                      showMoreEnabled
                      sx={{ width: '100%', whiteSpace: 'pre-wrap' }}
                    />
                  </Box>
                </>
              )}

              {(Array.isArray(event.attendees) && event.attendees.length > 0) && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {t('calendar.editor.attendees')}
                    </Typography>

                    <Stack spacing={1}>
                      {event.attendees.slice(0, 5).map((attendee) => (
                        <Box key={attendee.email} sx={{ display: 'flex', alignItems: 'center' }}>
                          <Iconify icon="mdi:account-circle-outline" sx={{ mr: 1, width: 20, height: 20, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {attendee.first_name} {attendee.last_name} ({attendee.email})
                          </Typography>
                        </Box>
                      ))}
                      {event.attendees.length > 5 && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                          {t('calendar.editor.attendees_more', { count: event.attendees.length - 5 })}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('common.created_at')}: {format(new Date(event.created_at), 'MMM d, yyyy')}
                </Typography>
                {event.updated_at && (
                  <Typography variant="caption" color="text.secondary">
                    {t('common.modified')}: {format(new Date(event.updated_at), 'MMM d, yyyy')}
                  </Typography>
                )}
              </Box>
            </Stack>
          )
        }

        {(!isLoading && !event) && (
          <Stack sx={{ textAlign: 'center', py: 4 }}>
            <EmptyContent />
            <Typography variant="h6" color="text.secondary">
              {t('calendar.event_not_found')}
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose}>
          {t('common.close')}
        </Button>

        <Button
          onClick={handleEditEvent}
          variant="outlined"
          startIcon={<Iconify icon="mdi:pencil" />}
          disabled={!event || event?.visibility === CalendarEventVisibility.PRIVATE}
        >
          {t('calendar.editor.edit_event')}
        </Button>


        <Button
          onClick={deleteConfirmControl.onTrue}
          variant="outlined"
          startIcon={<Iconify icon="mdi:trash-can-outline" />}
          color="error"
          disabled={!event}
        >
          {t('calendar.editor.delete_event')}
        </Button>

      </DialogActions>

      {
        event && (
          <CalendarEventEditor
            open={eventEditorControl.value}
            onClose={handleCloseEditor}
            eventId={event.id}
            calendarId={event.calendar.id}
          />
        )
      }

      <ConfirmDialog
        open={deleteConfirmControl.value}
        onClose={deleteConfirmControl.onFalse}
        title={t('calendar.delete_confirm.title')}
        content={t('calendar.delete_confirm.content')}
        action={
          <LoadingButton loading={isDeleting} variant="contained" color="error" onClick={handleDeleteEvent}>
            {t('common.delete')}
          </LoadingButton>
        }
      />

    </Dialog>
  );
}